import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import {
  readTenantCache,
  readTenantCacheUpdatedAt,
  writeTenantCache,
} from '../utils/lobbyQueryCache';

export interface TenantConfig {
  id: string;
  name: string;
  code: string;
  gamEnabled: boolean;
  gamNetworkId: string | null;
}

/**
 * Fetch tenant configuration by slug. Mirrors the shape used by apps/squares
 * and apps/frontend so the GAM fields (gamEnabled, gamNetworkId) are sourced
 * consistently across frontends.
 */
export const useGetTenant = (slug: string) => {
  return useQuery({
    queryKey: ['tenant', slug],
    queryFn: async (): Promise<TenantConfig> => {
      const res = await apiClient.get<{ tenant?: Record<string, unknown> }>(`/api/tenants/${slug}`);
      const t = res.data?.tenant;
      if (!t) throw new Error('Tenant not found');
      const config: TenantConfig = {
        id: String(t.id),
        name: String(
          (t as { nameEn?: string }).nameEn || (t as { title?: string }).title || t.code || ''
        ),
        code: String(t.code || ''),
        gamEnabled: Boolean((t as { gamEnabled?: boolean }).gamEnabled),
        gamNetworkId: (t as { gamNetworkId?: string | null }).gamNetworkId ?? null,
      };
      writeTenantCache(slug, config);
      return config;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    placeholderData: () => readTenantCache<TenantConfig>(slug),
    initialDataUpdatedAt: () => readTenantCacheUpdatedAt(slug),
    retry: 1,
  });
};
